import type { PlayerPublicState } from "@hitster/shared";
import type { PlayerColor } from "../lib/playerColors";
import { Timeline } from "./Timeline";

interface Props {
  player: PlayerPublicState;
  winTarget: number;
  isMe: boolean;
  canPlace: boolean;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  compact?: boolean;
  hideTimeline?: boolean;
  playerColor?: PlayerColor;
  showColorDot?: boolean;
}

export function PlayerBoardRow({
  player,
  winTarget,
  isMe,
  canPlace,
  selectedIndex,
  onSelect,
  compact: compactLayout,
  hideTimeline = false,
  playerColor,
  showColorDot = false,
}: Props) {
  const interactive = isMe && canPlace && !player.hasWon;
  const compactCards = compactLayout ?? !player.isActive;

  const panelStyle =
    !hideTimeline && playerColor
      ? {
          background: `linear-gradient(145deg, ${playerColor.rowBackground}, rgba(15, 10, 24, 0.88))`,
          borderColor: playerColor.rowBorder,
        }
      : undefined;

  return (
    <section
      className={`cw-panel p-2 sm:p-4 transition-all ${
        player.isActive
          ? "ring-2 ring-orange-400/70 shadow-lg shadow-orange-500/10"
          : "opacity-95"
      } ${hideTimeline ? "py-2 sm:py-3" : ""}`}
      style={panelStyle}
      aria-label={hideTimeline ? `Score de ${player.nickname}` : `Frise de ${player.nickname}`}
    >
      {!hideTimeline && (
        <Timeline
          timeline={player.timeline}
          selectedIndex={interactive ? selectedIndex : null}
          canPlace={interactive}
          onSelect={onSelect}
          compact={compactCards}
        />
      )}

      <div className={`${hideTimeline ? "" : "mt-1.5 sm:mt-2 pt-1.5 sm:pt-2 border-t border-violet-500/20"} flex flex-wrap items-center justify-between gap-1`}>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap min-w-0">
          {showColorDot && playerColor && (
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ backgroundColor: playerColor.dot }}
              aria-hidden
            />
          )}
          <span className={`font-semibold text-sm sm:text-base truncate ${player.isActive ? "text-orange-300" : "text-violet-100"}`}>
            {player.nickname}
            {isMe && <span className="text-violet-400/60 font-normal text-xs sm:text-sm ml-1">(vous)</span>}
          </span>
          {player.isHost && (
            <span className="text-[9px] sm:text-[10px] uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300">
              Hôte
            </span>
          )}
          {player.hasLeft && (
            <span className="text-[9px] sm:text-[10px] uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full bg-red-500/20 text-red-300">
              A quitté
            </span>
          )}
          {player.hasWon && (
            <span className="text-[9px] sm:text-[10px] uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded-full bg-green-500/20 text-green-300">
              Gagnant
            </span>
          )}
          {player.isActive && (
            <span className="hidden sm:inline text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-violet-500/30 text-violet-200">
              En jeu
            </span>
          )}
          {!player.connected && (
            <span className="text-[9px] sm:text-[10px] text-red-400">Hors ligne</span>
          )}
        </div>
        <p className="font-mono text-sm sm:text-lg tabular-nums">
          <span className={player.score >= winTarget ? "text-green-400" : "text-orange-400"}>{player.score}</span>
          <span className="text-violet-400/50">/{winTarget}</span>
        </p>
      </div>
    </section>
  );
}
